import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";

import CaretBottom from "../../../icons/CaretBottom";
import Delete from "../../../icons/Delete";
import Undo from "../../../icons/Undo";
import Warning from "../../../icons/Warning";
import Info from "../../../icons/Info";
import StatusBadge from "../../components/StatusBadge";
import { ParameterInput } from "../../components/TrackingInputs";
import { seedValueForProperty } from "../../utils/valueType";

import {
  useSpecificationStateStore,
  getItemMetadata,
  type ChangeTarget,
  type Change,
} from "./specificationState";
import {
  getAllowedProcessTemplateUuids,
  getParameterConstraints,
  getProcessOwnConstraints,
  useValidationStateStore,
  useViolationSummary,
} from "./validationState";
import {
  getProcessAtPath,
  isFieldRequiredByConstraint,
  processNameMatches,
  processSubtreeMatches,
  sortedStepIndices,
} from "../../utils/conditions";
import {
  useGetTemplates,
  useGetProcesses,
} from "../../../hooks/queries/useSpecQueries";

import {
  Section,
  SectionHeader,
  HorizontalPropertyRow,
  PropertyLabel,
  PropertyValue,
  DeleteButton,
  UndoButton,
  NoDataText,
  AddButton,
  AddRow,
  CollapsibleContainer,
  CollapsibleRow,
  CollapsibleRowLeft,
  CollapsibleName,
  CollapsibleCount,
  CollapsibleContent,
  NewPropertyRow,
} from "../../components/SectionComponents";

/**
 * How many levels of steps are shown/editable beneath a top-level process.
 * Top-level processes are depth 0, so steps live at depth 1..MAX_STEP_DEPTH.
 */
const MAX_STEP_DEPTH = 2;

const pathKey = (processPath: number[]) => processPath.join(">");

// Mirrors BOMNavigation's dashed depth line so nesting reads the same way.
const StepsContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1.5, 1.5, 1.5, 2),
  paddingLeft: theme.spacing(2),
  backgroundImage: `repeating-linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2) 0px,
    rgba(0, 0, 0, 0.2) 4px,
    transparent 4px,
    transparent 8px
  )`,
  backgroundSize: "2px 100%",
  backgroundRepeat: "no-repeat",
}));

const StepsHeader = styled(Typography)(({ theme }) => ({
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: "#666",
  marginBottom: theme.spacing(1),
}));

const StepRow = styled(CollapsibleRow)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: 0,
}));

const StepMeta = styled(Typography)({
  fontSize: "13px",
  color: "#777",
});

const selectSx = {
  fontSize: "14px",
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d0d0d0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b0b0b0",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1976d2",
  },
};

type PendingStep = { parentPath: number[]; processUuid: string };

/** Inherited state passed down a process subtree. */
type NodeFlags = {
  /** This process (or an ancestor) is pending deletion. */
  isDeleted: boolean;
  /** This process (or an ancestor) is newly added. */
  isNew: boolean;
  /** This process (or an ancestor) matched the search by name. */
  showAll: boolean;
};

type ProcessesSectionProps = {
  item: ArenaOnlyItem | Item;
  filteredProcesses: { proc: ProcessWithSteps; originalIndex: number }[];
  activeProcessIndex: number;
  expandedProcesses: Set<number>;
  onProcessTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  onToggleProcessExpanded: (index: number) => void;
  matchesSearch: (text: string) => boolean;
  sectionRef?: React.RefObject<HTMLDivElement>;
  isParentNew?: boolean;
  readOnly?: boolean;
};

const ProcessesSection = ({
  item,
  filteredProcesses,
  activeProcessIndex,
  expandedProcesses,
  onProcessTabChange,
  onToggleProcessExpanded,
  matchesSearch,
  sectionRef,
  isParentNew = false,
  readOnly = false,
}: ProcessesSectionProps) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  // Path key of the process whose "add parameter" dropdown is open.
  const [addingParameterFor, setAddingParameterFor] = useState<string | null>(
    null
  );
  // Path key of the process whose "add step" dropdown is open.
  const [addingStepFor, setAddingStepFor] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(
    () => new Set()
  );
  const [pendingProcessUuids, setPendingProcessUuids] = useState<string[]>([]);
  const [pendingSteps, setPendingSteps] = useState<PendingStep[]>([]);
  const [dismissedUserDefined, setDismissedUserDefined] = useState<Set<string>>(
    () => new Set()
  );
  const [searchParams] = useSearchParams();

  const { partNumber, uuid } = getItemMetadata(item);

  // Get current path from URL params
  const currentPath = searchParams.get("bom-item") || "";
  const condition = useSpecificationStateStore((state) => state.condition);
  const constraints = useValidationStateStore((state) => state.constraints);
  const violationSummary = useViolationSummary();
  const formulaDependencies = useValidationStateStore(
    (state) => state.formulaDependencies
  );
  const attributeUuidTranslator = useValidationStateStore(
    (state) => state.attributeUuidTranslator
  );
  const trackChange = useSpecificationStateStore((state) => state.trackChange);
  const getChange = useSpecificationStateStore((state) => state.getChange);
  const removeChange = useSpecificationStateStore(
    (state) => state.removeChange
  );
  const appendProcess = useSpecificationStateStore(
    (state) => state.appendProcess
  );
  const appendStep = useSpecificationStateStore((state) => state.appendStep);

  const { processes: fetchedProcesses, isLoading: isFetchingProcesses } =
    useGetProcesses(pendingProcessUuids);
  const { processes: fetchedSteps, isLoading: isFetchingSteps } =
    useGetProcesses(pendingSteps.map((step) => step.processUuid));

  // Helper to get current item from path - must use same navigation as ItemDetails
  const getCurrentItemFromPath = useMemo(() => {
    if (!condition) return null;

    const bomItemPath = currentPath;
    if (!bomItemPath) return condition;

    // Parse the path segments (e.g., "0>1>2" for absolute BOM indices)
    const indices = bomItemPath
      .split(">")
      .map((segment) => parseInt(segment, 10));

    // Navigate to the item at the path using absolute indices
    let currentItem: ItemWithBOM = condition;
    for (const index of indices) {
      if (index < 0 || index >= currentItem.bom.length) {
        return null;
      }
      currentItem = currentItem.bom[index];
    }

    return currentItem;
  }, [condition, currentPath]);

  // Get process_changes for the current item
  const processChanges = useMemo(() => {
    return getCurrentItemFromPath?.process_changes || [];
  }, [getCurrentItemFromPath]);

  /** The ProcessWithSteps at `processPath` within the current item. */
  const getEntry = useCallback(
    (processPath: number[]): ProcessWithSteps | null => {
      const location = getProcessAtPath(
        getCurrentItemFromPath?.processes || [],
        processPath
      );
      return location ? location.list[location.index] : null;
    },
    [getCurrentItemFromPath]
  );

  // When processes are fetched, append them to the item and clear from pending
  useEffect(() => {
    if (
      pendingProcessUuids.length > 0 &&
      !isFetchingProcesses &&
      fetchedProcesses.length > 0
    ) {
      fetchedProcesses.forEach((fetchedProcess) => {
        appendProcess(currentPath, fetchedProcess);
      });
      // Clear pending processes after appending
      setPendingProcessUuids([]);
    }
  }, [
    fetchedProcesses,
    isFetchingProcesses,
    pendingProcessUuids,
    appendProcess,
    currentPath,
  ]);

  // Same for steps, appended under their parent process in request order.
  useEffect(() => {
    if (
      pendingSteps.length > 0 &&
      !isFetchingSteps &&
      fetchedSteps.length > 0
    ) {
      pendingSteps.forEach(({ parentPath, processUuid }) => {
        const fetched = fetchedSteps.find(
          (step) => step.process.uuid === processUuid
        );
        if (fetched) {
          appendStep(currentPath, parentPath, fetched);
        }
      });
      setPendingSteps([]);
    }
  }, [fetchedSteps, isFetchingSteps, pendingSteps, appendStep, currentPath]);

  // Reset local UI state when the item changes
  useEffect(() => {
    setIsAddingNew(false);
    setAddingParameterFor(null);
    setAddingStepFor(null);
    setExpandedSteps(new Set());
    setDismissedUserDefined(new Set());
  }, [partNumber, uuid]);

  const handleDeleteProcess = (
    processIndex: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    const proc = getCurrentItemFromPath?.processes?.[processIndex];
    if (!proc) return;

    const target: ChangeTarget = {
      type: "processes",
      path: currentPath,
      processIndex,
      addedProcessUuid: proc.process.uuid || proc.process.arena_part_number,
    };

    const change: Change = {
      target,
      operation: "delete",
      currentValue: undefined,
      previousValue: undefined,
      timestamp: Date.now(),
    };

    // Track the deletion — trackChange will either remove completely
    // (if newly added) or mark as deleted (if existing)
    trackChange(change);

    // Also remove from pending processes if it hasn't been fetched yet
    const processUuid = proc.process.uuid || proc.process.arena_part_number;
    setPendingProcessUuids((prev) =>
      prev.filter((uuid) => uuid !== processUuid)
    );
  };

  const handleUndoProcess = (processIndex: number, event: React.MouseEvent) => {
    event.stopPropagation();

    const proc = getCurrentItemFromPath?.processes?.[processIndex];
    if (!proc) return;

    const target: ChangeTarget = {
      type: "processes",
      path: currentPath,
      processIndex,
      addedProcessUuid: proc.process.uuid || proc.process.arena_part_number,
    };

    // Remove the deletion change to restore the process
    removeChange(target);
  };

  const isProcessDeleted = useCallback(
    (processIndex: number): boolean => {
      return processChanges.some(
        (change) => change.is_deleted && change.index === processIndex
      );
    },
    [processChanges]
  );

  const isProcessNew = (processIndex: number): boolean => {
    // If parent is new, all processes appear as new
    if (isParentNew) {
      return true;
    }

    return processChanges.some(
      (c) => c.index === processIndex && c.is_added && !c.is_deleted
    );
  };

  // ---------------------------------------------------------------------------
  // Steps (nested processes)
  // ---------------------------------------------------------------------------

  const stepChangeAt = (parentPath: number[], stepIndex: number) =>
    (getEntry(parentPath)?.step_changes || []).find(
      (c) => c.index === stepIndex
    );

  const isStepDeleted = (parentPath: number[], stepIndex: number) =>
    stepChangeAt(parentPath, stepIndex)?.is_deleted === true;

  const isStepNew = (parentPath: number[], stepIndex: number) => {
    const change = stepChangeAt(parentPath, stepIndex);
    return change?.is_added === true && !change.is_deleted;
  };

  const makeStepTarget = (
    stepPath: number[],
    step: ProcessWithSteps
  ): ChangeTarget => ({
    type: "steps",
    path: currentPath,
    processPath: stepPath.slice(0, -1),
    stepIndex: stepPath[stepPath.length - 1],
    addedProcessUuid: step.process.uuid || step.process.arena_part_number,
  });

  const handleDeleteStep = (
    stepPath: number[],
    step: ProcessWithSteps,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    trackChange({
      target: makeStepTarget(stepPath, step),
      operation: "delete",
      currentValue: undefined,
      previousValue: undefined,
      timestamp: Date.now(),
    });
    // Dropping a pending addition shifts later siblings, so collapse state
    // keyed by path would point at the wrong steps; reset it.
    setExpandedSteps(new Set());
  };

  const handleUndoStep = (
    stepPath: number[],
    step: ProcessWithSteps,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    removeChange(makeStepTarget(stepPath, step));
  };

  const toggleStepExpanded = (stepPath: number[]) => {
    const key = pathKey(stepPath);
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Allowed step templates for every process that can receive steps (depth <
  // MAX_STEP_DEPTH), keyed by process path. Constraints come from each
  // parent process's own template (`process_constraints` on its entry).
  const allowedStepTemplatesByPath = useMemo(() => {
    const result = new Map<string, string[]>();
    const visit = (entries: ProcessWithSteps[], parentPath: number[]) => {
      entries.forEach((entry, index) => {
        const processPath = [...parentPath, index];
        if (processPath.length - 1 >= MAX_STEP_DEPTH) return;
        const steps = entry.steps || [];
        const deleted = new Set(
          (entry.step_changes || [])
            .filter((c) => c.is_deleted)
            .map((c) => c.index)
        );
        const allowed = getAllowedProcessTemplateUuids(
          getProcessOwnConstraints(constraints, entry.process.uuid),
          steps,
          (i) => deleted.has(i)
        );
        if (allowed.length > 0) {
          result.set(pathKey(processPath), allowed);
        }
        visit(steps, processPath);
      });
    };
    visit(getCurrentItemFromPath?.processes || [], []);
    return result;
  }, [constraints, getCurrentItemFromPath]);

  const allStepTemplateUuids = useMemo(
    () =>
      Array.from(
        new Set(Array.from(allowedStepTemplatesByPath.values()).flat())
      ),
    [allowedStepTemplatesByPath]
  );
  const { templates: stepTemplates, isLoading: isLoadingStepTemplates } =
    useGetTemplates(allStepTemplateUuids);

  const getStepTemplateOptions = (processPath: number[]) => {
    const allowed = allowedStepTemplatesByPath.get(pathKey(processPath)) || [];
    return stepTemplates.filter(
      (template) =>
        allowed.includes(template.uuid) ||
        allowed.includes(template.template_definition_uuid)
    );
  };

  const handleStepSelect = (parentPath: number[], templateUuid: string) => {
    const template = stepTemplates.find((t) => t.uuid === templateUuid);
    const parent = getEntry(parentPath);
    if (!template?.base_process_uuid || !parent) {
      return;
    }
    const processUuid = template.base_process_uuid;
    const parentKey = pathKey(parentPath);
    // Account for siblings still being fetched so indices don't collide.
    const pendingForParent = pendingSteps.filter(
      (step) => pathKey(step.parentPath) === parentKey
    ).length;

    trackChange({
      target: {
        type: "steps",
        path: currentPath,
        processPath: parentPath,
        stepIndex: (parent.steps || []).length + pendingForParent,
        addedProcessUuid: processUuid,
      },
      operation: "add",
      currentValue: undefined,
      previousValue: undefined,
      timestamp: Date.now(),
    });
    setPendingSteps((prev) => [...prev, { parentPath, processUuid }]);
    setAddingStepFor(null);
  };

  // ---------------------------------------------------------------------------
  // Parameters (keyed by process path so they work at any depth)
  // ---------------------------------------------------------------------------

  // Get parameters that have been added for a specific process
  const getAddedParameters = (processPath: number[]): [string, ValueType][] => {
    const entry = getEntry(processPath);
    return (entry?.process_changes || [])
      .filter((change: PropertyChange) => change.is_added)
      .map((change) => [change.name, (change.new_value ?? "") as ValueType]);
  };

  const findParameterChange = (processPath: number[], parameterName: string) =>
    (getEntry(processPath)?.process_changes || []).find(
      (c: PropertyChange) => c.name === parameterName
    );

  // Get available parameters for a process
  const getAvailableParameters = (
    proc: Process,
    existingParameterNames: Set<string>
  ) => {
    if (!proc.process_type || !proc.process_type.parameters) {
      return [];
    }

    return proc.process_type.parameters
      .filter((param) => !existingParameterNames.has(param.name))
      .map((param) => param.name);
  };

  const userDefinedKey = (processPath: number[], parameterName: string) =>
    `${pathKey(processPath)}:${parameterName}`;

  // User-defined parameters with no value yet (not already shown, not
  // dismissed, matching the current search). Nothing is in the store for
  // these until the user edits the input; the first edit tracks an "add"
  // and writes the value into process.parameters, so the row becomes a
  // regular added parameter and drops out of this list automatically.
  const getPendingUserDefinedParameters = (
    proc: Process,
    processPath: number[],
    existingParameterNames: Set<string>,
    matches: (text: string) => boolean
  ): [string, ValueType][] => {
    const definitions = proc.process_type?.parameters || [];
    return (proc.user_defined_parameters || [])
      .filter(
        (name) =>
          matches(name) &&
          !existingParameterNames.has(name) &&
          !dismissedUserDefined.has(userDefinedKey(processPath, name))
      )
      .map((name) => [
        name,
        seedValueForProperty(definitions.find((param) => param.name === name)),
      ]);
  };

  const makeParameterTarget = (
    processPath: number[],
    parameterName: string
  ): ChangeTarget => ({
    type: "parameter",
    path: currentPath,
    processPath,
    propertyName: parameterName,
  });

  const handleParameterSelect = (
    processPath: number[],
    parameterName: string
  ) => {
    if (parameterName && uuid) {
      const change: Change = {
        target: makeParameterTarget(processPath, parameterName),
        operation: "add",
        currentValue: "",
        previousValue: undefined,
        timestamp: Date.now(),
      };
      trackChange(change);
      setAddingParameterFor(null);
    }
  };

  const handleDeleteParameter = (
    processPath: number[],
    parameterName: string,
    currentValue: any
  ) => {
    const target = makeParameterTarget(processPath, parameterName);

    // Check if this is a newly added parameter
    const existingChange = getChange(target);

    // An untouched user-defined row has nothing in the change model to
    // remove, so just hide it; the parameter returns to the "+" dropdown.
    const process = getEntry(processPath)?.process;
    const isPendingUserDefined =
      !existingChange &&
      !!process?.user_defined_parameters?.includes(parameterName) &&
      !(parameterName in (process.parameters || {}));
    if (isPendingUserDefined) {
      setDismissedUserDefined((prev) =>
        new Set(prev).add(userDefinedKey(processPath, parameterName))
      );
      return;
    }

    if (existingChange && existingChange.operation === "add") {
      // If it was newly added, just remove it from changes completely.
      // removeChange also strips the pending value from process.parameters,
      // so a user-defined parameter reverts to an empty row.
      removeChange(target);
    } else {
      // If it's an existing parameter, track as deletion
      const change: Change = {
        target,
        operation: "delete",
        currentValue: undefined,
        previousValue: currentValue,
        timestamp: Date.now(),
      };
      trackChange(change);
    }
  };

  const handleUndoParameter = (
    processPath: number[],
    parameterName: string
  ) => {
    // Remove the deletion change to restore the parameter
    removeChange(makeParameterTarget(processPath, parameterName));
  };

  // Get units for a parameter
  const getParameterUnits = (
    proc: Process,
    parameterName: string
  ): string | null => {
    if (!proc.process_type || !proc.process_type.parameters) {
      return null;
    }
    const parameter = proc.process_type.parameters.find(
      (param) => param.name === parameterName
    );
    if (
      parameter &&
      parameter.units &&
      parameter.units.toLowerCase() !== "each"
    ) {
      return parameter.units;
    }
    return null;
  };

  // Check if a parameter is required, either by the process type
  // (`is_required`) or by a template-level `required_parameter` constraint.
  const isParameterRequired = (
    proc: Process,
    parameterName: string
  ): boolean => {
    if (!proc.process_type?.parameters) return false;
    const param = proc.process_type.parameters.find(
      (p) => p.name === parameterName
    );
    if (param?.is_required) return true;
    if (!proc.uuid) return false;
    return isFieldRequiredByConstraint(
      getParameterConstraints(constraints, proc.uuid, parameterName)
    );
  };

  // Check if a parameter is computed from a formula
  const getParameterFormula = (
    proc: Process,
    parameterName: string
  ): string | null => {
    // Check all formula dependencies to see if this parameter is a target
    const processUuid = proc.uuid || proc.arena_part_number;

    for (const dependencies of Object.values(formulaDependencies)) {
      for (const dep of dependencies) {
        if (
          dep.targetItemUuid === processUuid &&
          dep.targetAttributeName === parameterName
        ) {
          // Replace variable aliases with actual attribute names
          let displayFormula = dep.formula;
          dep.variables.forEach((variable) => {
            const sourceLocations = attributeUuidTranslator.get(
              variable.attribute_uuid
            );
            if (sourceLocations && sourceLocations.length > 0) {
              const regex = new RegExp(`\\b${variable.alias}\\b`, "g");
              displayFormula = displayFormula.replace(
                regex,
                sourceLocations[0].attributeName
              );
            }
          });
          return displayFormula;
        }
      }
    }
    return null;
  };

  // Format parameter name with units if applicable
  const formatParameterName = (
    proc: Process,
    parameterName: string
  ): React.ReactNode => {
    const units = getParameterUnits(proc, parameterName);
    const formula = getParameterFormula(proc, parameterName);
    const label = units ? `${parameterName} (${units})` : parameterName;

    if (formula) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {label}
          <Tooltip title={`Computed field: ${formula}`} placement="right">
            <Box sx={{ display: "flex", alignItems: "center", cursor: "help" }}>
              <Info style={{ width: "16px", height: "16px", fill: "#666" }} />
            </Box>
          </Tooltip>
        </Box>
      );
    }

    return label;
  };

  // Check if a parameter has any violations using path-based lookup.
  // Steps are matched by process UUID only; their top-level index would
  // collide with the top-level process at that index.
  const hasRangeViolation = (
    processPath: number[],
    parameterName: string,
    processUuid?: string | null
  ): boolean => {
    return violationSummary
      .getParameterViolationsByPath(
        currentPath,
        processPath.length === 1 ? processPath[0] : -1,
        parameterName,
        processUuid
      )
      .some((violation) => violation.constraint_type === "parameter_range");
  };

  // Allowed top-level templates, filtering out templates that have reached
  // max_occurrences to avoid unnecessary API calls.
  const allowedTemplateUuids = useMemo(() => {
    if (!constraints || !uuid) return [];

    const itemConstraints = constraints.item_constraints.find(
      (ic) => ic.item_uuid === uuid
    );
    if (!itemConstraints) return [];

    return getAllowedProcessTemplateUuids(
      itemConstraints.process_constraints,
      getCurrentItemFromPath?.processes || [],
      isProcessDeleted
    );
  }, [constraints, uuid, getCurrentItemFromPath, isProcessDeleted]);

  // Fetch templates for the allowed template UUIDs
  // (constraint-based filtering already done in allowedTemplateUuids)
  const { templates: availableTemplates, isLoading: isLoadingTemplates } =
    useGetTemplates(allowedTemplateUuids);

  const handleAddClick = () => {
    setIsAddingNew(true);
  };

  const handleProcessSelect = (templateUuid: string) => {
    const template = availableTemplates?.find((t) => t.uuid === templateUuid);

    if (!template?.base_process_uuid) {
      return;
    }

    const processUuid = template.base_process_uuid;

    // The new process will be added at the end of the entire processes array
    const newProcessIndex =
      (getCurrentItemFromPath?.processes?.length || 0) +
      pendingProcessUuids.length;

    const changeTarget: ChangeTarget = {
      type: "processes",
      path: currentPath,
      processIndex: newProcessIndex,
      addedProcessUuid: processUuid,
    };

    const change: Change = {
      target: changeTarget,
      operation: "add",
      currentValue: undefined,
      previousValue: undefined,
      timestamp: Date.now(),
    };

    // Track the change
    trackChange(change);

    // Add to pending processes to trigger fetch via useGetProcesses hook
    setPendingProcessUuids((prev) => [...prev, processUuid]);

    // Close the add mode
    setIsAddingNew(false);
  };

  // Check if adding new processes is allowed based on AllowedProcessTemplatesConstraint
  const canAddProcess = () => {
    return allowedTemplateUuids.length > 0 && availableTemplates.length > 0;
  };

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  /** Parameters shown for a process, honoring search unless `showAll`. */
  const collectParameters = (
    entry: ProcessWithSteps,
    processPath: number[],
    showAll: boolean
  ) => {
    const matches = showAll ? () => true : matchesSearch;
    const existingParameters = Object.entries(
      entry.process.parameters || {}
    ).filter(([key]) => matches(key));
    // Added parameters are also written into process.parameters by
    // applyPropertyEdit, so dedupe by name to avoid double rows.
    const existingNames = new Set(existingParameters.map(([key]) => key));
    const addedParameters = getAddedParameters(processPath).filter(
      ([key]) => !existingNames.has(key) && matches(key)
    );
    const valuedParameters = [...existingParameters, ...addedParameters];
    const pendingUserDefinedParameters = getPendingUserDefinedParameters(
      entry.process,
      processPath,
      new Set(valuedParameters.map(([key]) => key)),
      matches
    );
    return [...valuedParameters, ...pendingUserDefinedParameters].sort((a, b) =>
      a[0].localeCompare(b[0])
    );
  };

  const renderParameters = (
    entry: ProcessWithSteps,
    processPath: number[],
    allParameters: [string, ValueType][],
    flags: NodeFlags
  ) => {
    const proc = entry.process;
    const key = pathKey(processPath);
    const isAddingParameter = addingParameterFor === key;
    // Every parameter the process has (not just search matches) counts as
    // existing, so the "+" dropdown never offers a duplicate.
    const existingParameterNames = new Set([
      ...Object.keys(proc.parameters || {}),
      ...allParameters.map(([name]) => name),
    ]);
    const availableParameters = getAvailableParameters(
      proc,
      existingParameterNames
    );

    return (
      <>
        {allParameters.length > 0 ? (
          allParameters.map(([name, value]) => {
            const change = findParameterChange(processPath, name);
            const isParamDeleted = change?.is_deleted === true;
            const isAdded = flags.isNew || change?.is_added === true;
            const isRequired = isParameterRequired(proc, name);
            return (
              <HorizontalPropertyRow
                key={name}
                isDeleted={isParamDeleted}
                isAdded={isAdded}
              >
                <PropertyLabel isDeleted={isParamDeleted}>
                  {hasRangeViolation(processPath, name, proc.uuid) && (
                    <Warning
                      style={{
                        fontSize: "16px",
                        color: "#ff9800",
                        marginRight: "8px",
                      }}
                    />
                  )}
                  {formatParameterName(proc, name)}
                </PropertyLabel>
                <PropertyValue>
                  <ParameterInput
                    item={item}
                    process={proc}
                    processIndex={processPath[0]}
                    processPath={processPath}
                    parameterName={name}
                    parameterValue={{ value }}
                    disabled={isParamDeleted || flags.isDeleted || readOnly}
                    units={getParameterUnits(proc, name)}
                  />
                  {isParamDeleted ? (
                    <UndoButton
                      size="small"
                      onClick={() => handleUndoParameter(processPath, name)}
                      aria-label={`Undo delete ${name}`}
                      disabled={flags.isDeleted || readOnly}
                    >
                      <Undo />
                    </UndoButton>
                  ) : (
                    <Tooltip
                      title={isRequired ? "This parameter is required" : ""}
                      disableHoverListener={!isRequired}
                    >
                      <span>
                        <DeleteButton
                          size="small"
                          onClick={() =>
                            handleDeleteParameter(processPath, name, value)
                          }
                          aria-label={`Delete ${name}`}
                          disabled={flags.isDeleted || readOnly || isRequired}
                        >
                          <Delete />
                        </DeleteButton>
                      </span>
                    </Tooltip>
                  )}
                </PropertyValue>
              </HorizontalPropertyRow>
            );
          })
        ) : !isAddingParameter && !flags.isDeleted ? (
          <NoDataText>No parameters defined</NoDataText>
        ) : null}

        {isAddingParameter && !flags.isDeleted ? (
          <NewPropertyRow>
            <Box sx={{ minWidth: "220px", paddingLeft: 1, paddingRight: 1 }}>
              <Select
                size="small"
                value=""
                onChange={(e) =>
                  handleParameterSelect(processPath, e.target.value)
                }
                displayEmpty
                fullWidth
                autoFocus
                disabled={readOnly}
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  <em>Select parameter...</em>
                </MenuItem>
                {availableParameters.map((paramName) => (
                  <MenuItem key={paramName} value={paramName}>
                    {paramName}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </NewPropertyRow>
        ) : availableParameters.length > 0 && !flags.isDeleted ? (
          <AddRow
            onClick={readOnly ? undefined : () => setAddingParameterFor(key)}
            disabled={readOnly}
          >
            <AddButton disabled={readOnly} aria-label="Add parameter">
              +
            </AddButton>
          </AddRow>
        ) : null}
      </>
    );
  };

  /** The steps list (and "Add step") beneath a process at `depth`. */
  const renderSteps = (
    entry: ProcessWithSteps,
    processPath: number[],
    depth: number,
    flags: NodeFlags
  ) => {
    const steps = entry.steps || [];

    if (depth >= MAX_STEP_DEPTH) {
      return steps.length > 0 ? (
        <NoDataText>
          {steps.length} further nested {steps.length === 1 ? "step" : "steps"}{" "}
          not shown
        </NoDataText>
      ) : null;
    }

    const key = pathKey(processPath);
    const stepOptions = getStepTemplateOptions(processPath);
    const canAddStep =
      !flags.isDeleted &&
      (allowedStepTemplatesByPath.get(key)?.length ?? 0) > 0 &&
      (stepOptions.length > 0 || isLoadingStepTemplates);
    const isAddingStep = addingStepFor === key;
    const visibleStepIndices = sortedStepIndices(steps).filter(
      (index) =>
        flags.showAll || processSubtreeMatches(steps[index], matchesSearch)
    );

    if (steps.length === 0 && !canAddStep) {
      return null;
    }

    return (
      <StepsContainer>
        <StepsHeader>Steps ({steps.length})</StepsHeader>
        {visibleStepIndices.map((index) =>
          renderStep(steps[index], [...processPath, index], depth + 1, flags)
        )}

        {isAddingStep ? (
          <NewPropertyRow sx={{ borderTop: "none", borderRadius: "4px" }}>
            <Box sx={{ minWidth: "220px", paddingLeft: 1, paddingRight: 1 }}>
              <Select
                size="small"
                value=""
                onChange={(e) => handleStepSelect(processPath, e.target.value)}
                displayEmpty
                fullWidth
                autoFocus
                disabled={readOnly}
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  <em>Select step template...</em>
                </MenuItem>
                {isLoadingStepTemplates ? (
                  <MenuItem disabled>
                    <em>Loading templates...</em>
                  </MenuItem>
                ) : (
                  stepOptions.map((template) => (
                    <MenuItem key={template.uuid} value={template.uuid}>
                      {template.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </Box>
          </NewPropertyRow>
        ) : canAddStep ? (
          <AddRow
            onClick={readOnly ? undefined : () => setAddingStepFor(key)}
            disabled={readOnly}
            sx={{ border: "1px dashed #d0d0d0", borderRadius: "4px" }}
          >
            <AddButton disabled={readOnly} aria-label="Add step">
              +
            </AddButton>
            <StepMeta>Add step</StepMeta>
          </AddRow>
        ) : null}
      </StepsContainer>
    );
  };

  const renderStep = (
    step: ProcessWithSteps,
    stepPath: number[],
    depth: number,
    parentFlags: NodeFlags
  ): JSX.Element => {
    const parentPath = stepPath.slice(0, -1);
    const stepIndex = stepPath[stepPath.length - 1];
    const ownDeleted = isStepDeleted(parentPath, stepIndex);
    const flags: NodeFlags = {
      isDeleted: parentFlags.isDeleted || ownDeleted,
      isNew: parentFlags.isNew || isStepNew(parentPath, stepIndex),
      showAll: parentFlags.showAll || processNameMatches(step, matchesSearch),
    };
    const isExpanded = expandedSteps.has(pathKey(stepPath));
    const allParameters = collectParameters(step, stepPath, flags.showAll);
    const typeName = step.process.process_type?.name || "Arena process";
    const displayName = step.process.name || typeName;
    const nestedCount = (step.steps || []).length;

    return (
      <CollapsibleContainer
        key={pathKey(stepPath)}
        sx={{ marginBottom: 1, "&:last-of-type": { marginBottom: 1 } }}
      >
        <StepRow
          isExpanded={isExpanded}
          isAdded={flags.isNew}
          onClick={() => toggleStepExpanded(stepPath)}
          style={{
            opacity: flags.isDeleted ? 0.5 : 1,
            textDecoration: flags.isDeleted ? "line-through" : "none",
          }}
        >
          <CollapsibleRowLeft sx={{ gap: "12px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CollapsibleName sx={{ fontSize: "14px" }}>
                {displayName}
                {step.process.arena_part_number &&
                  ` (${step.process.arena_part_number})`}
              </CollapsibleName>
              {flags.isNew && <StatusBadge label="New" size="small" />}
            </Box>
            <StepMeta>
              {displayName !== typeName ? `${typeName} · ` : ""}Line #
              {step.line_number ?? "-"}
            </StepMeta>
            <CollapsibleCount sx={{ fontSize: "13px" }}>
              {allParameters.length}{" "}
              {allParameters.length === 1 ? "parameter" : "parameters"}
              {nestedCount > 0 &&
                ` · ${nestedCount} ${nestedCount === 1 ? "step" : "steps"}`}
            </CollapsibleCount>
          </CollapsibleRowLeft>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              sx={{
                color: "#666",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              <CaretBottom />
            </IconButton>
            {ownDeleted ? (
              <UndoButton
                size="small"
                onClick={(e) => handleUndoStep(stepPath, step, e)}
                aria-label={`Undo delete step ${displayName}`}
                disabled={parentFlags.isDeleted || readOnly}
              >
                <Undo />
              </UndoButton>
            ) : (
              <DeleteButton
                size="small"
                onClick={(e) => handleDeleteStep(stepPath, step, e)}
                aria-label={`Delete step ${displayName}`}
                disabled={parentFlags.isDeleted || readOnly}
              >
                <Delete />
              </DeleteButton>
            )}
          </Box>
        </StepRow>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CollapsibleContent>
            {renderParameters(step, stepPath, allParameters, flags)}
            {renderSteps(step, stepPath, depth, flags)}
          </CollapsibleContent>
        </Collapse>
      </CollapsibleContainer>
    );
  };

  return (
    <Section ref={sectionRef}>
      <SectionHeader>Processes</SectionHeader>
      {filteredProcesses.length > 0 ? (
        <>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#e0e0e0",
              marginBottom: 3,
            }}
          >
            <Tabs
              value={activeProcessIndex}
              onChange={onProcessTabChange}
              sx={{
                minHeight: "42px",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#666",
                  minHeight: "42px",
                  minWidth: "auto",
                  padding: "8px 16px",
                },
                "& .Mui-selected": {
                  color: "#1976d2 !important",
                  fontWeight: 500,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#1976d2",
                  height: "2px",
                },
              }}
            >
              {filteredProcesses.map(({ proc, originalIndex }) => {
                const isNew = isProcessNew(originalIndex);
                const isDeleted = isProcessDeleted(originalIndex);
                return (
                  <Tab
                    key={originalIndex}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          textDecoration: isDeleted ? "line-through" : "none",
                          opacity: isDeleted ? 0.5 : 1,
                        }}
                      >
                        {proc.process.process_type?.name} (Line #
                        {proc.line_number ?? "-"})
                        {isNew && <StatusBadge label="New" size="small" />}
                      </Box>
                    }
                    disabled={readOnly}
                  />
                );
              })}
            </Tabs>
          </Box>

          {filteredProcesses.map(({ proc, originalIndex }, displayIndex) => {
            const processPath = [originalIndex];
            const isNew = isProcessNew(originalIndex);
            const isDeleted = isProcessDeleted(originalIndex);
            const flags: NodeFlags = {
              isDeleted,
              isNew,
              showAll: processNameMatches(proc, matchesSearch),
            };
            const allParameters = collectParameters(
              proc,
              processPath,
              flags.showAll
            );
            const isExpanded = expandedProcesses.has(displayIndex);
            const stepCount = (proc.steps || []).length;

            return (
              <CollapsibleContainer key={originalIndex}>
                <CollapsibleRow
                  isExpanded={isExpanded}
                  onClick={() => onToggleProcessExpanded(displayIndex)}
                  style={{
                    opacity: isDeleted ? 0.5 : 1,
                    textDecoration: isDeleted ? "line-through" : "none",
                    cursor: readOnly ? "default" : undefined,
                  }}
                >
                  <CollapsibleRowLeft>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CollapsibleName>
                        {proc.process.process_type?.name}
                        {proc.process.arena_part_number &&
                          ` (${proc.process.arena_part_number})`}
                      </CollapsibleName>
                      {isNew && <StatusBadge label="New" size="small" />}
                    </Box>
                    <CollapsibleCount>
                      {allParameters.length}{" "}
                      {allParameters.length === 1 ? "parameter" : "parameters"}
                      {stepCount > 0 &&
                        ` · ${stepCount} ${stepCount === 1 ? "step" : "steps"}`}
                    </CollapsibleCount>
                  </CollapsibleRowLeft>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      size="small"
                      sx={{
                        color: "#666",
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    >
                      <CaretBottom />
                    </IconButton>

                    {isDeleted ? (
                      <UndoButton
                        size="small"
                        onClick={(e) => handleUndoProcess(originalIndex, e)}
                        aria-label="Undo delete process"
                        disabled={readOnly}
                      >
                        <Undo />
                      </UndoButton>
                    ) : (
                      <DeleteButton
                        size="small"
                        onClick={(e) => handleDeleteProcess(originalIndex, e)}
                        aria-label="Delete process"
                        disabled={readOnly}
                      >
                        <Delete />
                      </DeleteButton>
                    )}
                  </Box>
                </CollapsibleRow>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <CollapsibleContent>
                    {renderParameters(proc, processPath, allParameters, flags)}
                    {renderSteps(proc, processPath, 0, flags)}
                  </CollapsibleContent>
                </Collapse>
              </CollapsibleContainer>
            );
          })}
        </>
      ) : !isAddingNew ? (
        <NoDataText>No processes defined</NoDataText>
      ) : null}

      {isAddingNew && canAddProcess() ? (
        <NewPropertyRow>
          <Box sx={{ minWidth: "220px", paddingLeft: 1, paddingRight: 1 }}>
            <Select
              size="small"
              value=""
              onChange={(e) => handleProcessSelect(e.target.value)}
              displayEmpty
              fullWidth
              autoFocus
              disabled={readOnly}
              sx={selectSx}
            >
              <MenuItem value="" disabled>
                <em>Select process template...</em>
              </MenuItem>
              {isLoadingTemplates ? (
                <MenuItem disabled>
                  <em>Loading templates...</em>
                </MenuItem>
              ) : (
                availableTemplates.map((template) => (
                  <MenuItem key={template.uuid} value={template.uuid}>
                    {template.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </Box>
        </NewPropertyRow>
      ) : null}

      {!isAddingNew && canAddProcess() && (
        <AddRow
          onClick={readOnly ? undefined : handleAddClick}
          disabled={readOnly}
        >
          <AddButton disabled={readOnly} aria-label="Add process">
            +
          </AddButton>
          <StepMeta>Add process</StepMeta>
        </AddRow>
      )}
    </Section>
  );
};

export default ProcessesSection;
